import { ConfirmInterestStatus, useConfirmInterest } from "./useConfirmInterest";
import { Button, type ButtonSize } from "../../../../../../mads/Button";
import { useI18n } from "../../../../../../shared/I18n";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import { RnEmployerOutreachTst } from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { useEmployerOutreachInfo } from "../../../../hooks/useEmployerOutreachInfo";
import { useEmployerOutreachSource } from "../../../../hooks/useEmployerOutreachSource";
import {
  useEmployerOutreachUIConfig,
  OutreachStatus,
} from "../../../../hooks/useEmployerOutreachUIConfig";
import { useFullScreenVJActive } from "../../../../hooks/useFullScreenVJActive";

import React, { useCallback, useEffect, useState } from "react";
import { Platform } from "react-native";

export interface ConfirmInterestButtonProps {
  jobKey: string;
  onError?: () => void;
  onSuccess?: () => void;
  resetErrorRef?: React.MutableRefObject<(() => void) | null>;
  iconBefore?: React.JSX.Element;
  overrideButtonText?: string;
  buttonSize?: ButtonSize | undefined;
}

export function useIsConfirmInterestButtonVisible(jobKey: string): boolean {
  const { isFromEmployerOutreach, outreachType } = useEmployerOutreachSource();
  const isEmployerOutreachActive =
    useViewjobProctorAllocation(RnEmployerOutreachTst).value > 1 || Platform.OS === "web";
  const isFullScreenVJActive = useFullScreenVJActive();

  const isFastApply =
    outreachType === "FAST_APPLY_WITHOUT_INTERVIEW" || outreachType === "FAST_APPLY_WITH_INTERVIEW";

  // Check the status - don't show button if already confirmed
  const result = useEmployerOutreachInfo(jobKey);
  const isAlreadyConfirmed = result?.outreachInfo?.outreachStatus === OutreachStatus.INTERESTED;

  return (
    (isEmployerOutreachActive || isFullScreenVJActive) &&
    isFromEmployerOutreach &&
    isFastApply &&
    !isAlreadyConfirmed
  );
}

export function ConfirmInterestButton({
  jobKey,
  onError,
  onSuccess,
  resetErrorRef,
  iconBefore,
  overrideButtonText,
  buttonSize = "lg",
}: ConfirmInterestButtonProps): React.JSX.Element | null {
  const i18n = useI18n();
  const { confirmInterest, status, resetError } = useConfirmInterest(jobKey);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  // Get UI config for button text and disabled state
  const result = useEmployerOutreachInfo(jobKey);
  const outreachInfo = result?.outreachInfo ?? null;
  const uiConfig = useEmployerOutreachUIConfig(outreachInfo);

  // Check if button should be visible
  const shouldShowButton = useIsConfirmInterestButtonVisible(jobKey);

  // Assign resetError to the ref so parent can call it on dismiss
  useEffect(() => {
    if (resetErrorRef) {
      resetErrorRef.current = resetError;
    }
  }, [resetError, resetErrorRef]);

  useEffect(() => {
    if (status === ConfirmInterestStatus.Success) {
      setHasConfirmed(true);
      onSuccess?.();
    } else if (status === ConfirmInterestStatus.Error) {
      onError?.();
    }
  }, [status, onError, onSuccess]);

  const handleConfirmInterest = useCallback(() => {
    if (hasConfirmed) {
      return;
    }
    void confirmInterest();
  }, [confirmInterest, hasConfirmed]);

  if (!shouldShowButton) {
    return null;
  }

  // Use button text from UI config if available, otherwise fallback to default
  const buttonText =
    overrideButtonText ?? uiConfig.acceptButton?.text ?? i18n.gettext("Confirm my interest");
  const isButtonDisabled =
    status === ConfirmInterestStatus.Submitting || hasConfirmed || uiConfig.acceptButton?.disabled;
  const loading = status === ConfirmInterestStatus.Submitting;

  return (
    <Button
      onPress={handleConfirmInterest}
      variant="primary"
      disabled={isButtonDisabled}
      loading={loading}
      size={buttonSize}
      iconBefore={(!loading && iconBefore) ?? undefined}
    >
      {buttonText}
    </Button>
  );
}
